import { get } from 'lodash'
import React, { useState, useCallback, useEffect } from 'react'
import { useLatest } from 'react-use'
import { Link } from 'react-router-dom'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'

import ToggleButton from './ToggleButton'
import UserSearch from './UserSearch'
import useStyles from './useStyles'
import CheckOutDialog from './CheckOutDialog'
import { RequirementsModal } from 'civic-champs-shared/question-sets/components'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import useMiniSearch from 'utils/useMiniSearch'
import useDebounce from 'utils/useDebounce'
import useKioskSearchDiagnostics from './useKioskSearchDiagnostics'
import { useErrorNotification } from 'civic-champs-shared/api/hooks'
import { PrerequisitesAction } from 'civic-champs-shared/question-sets/types'
import { getEncodedOccurrence, getSearchResults } from 'kiosk/selectors'
import { searchVolunteersRequested, searchVolunteersCleared } from 'kiosk/actions'
import { useDispatch, useSelector } from 'react-redux'
import { useFeatureEnabled } from 'core/feature/hooks'

// Memoized config - no need to recreate on every render
const timesheetSearchConfig = {
  fields: ['user.givenName', 'user.familyName', 'user.email', 'user.phoneNumber', 'user.homePhoneNumber'],
  extractField: get,
  processTerm: (term, field) => (field === 'user.phoneNumber' ? term.substring(2) : term.toLowerCase()),
  searchOptions: {
    processTerm: term => term.toLowerCase(),
    prefix: true,
  },
}

//TODO better name
//TODO move to utilities?
const useRunningGuard = callback => {
  const [running, setRunning] = useState(false)
  const latestRunning = useLatest(running)

  const guardedCallback = useCallback(
    (...args) => {
      //TODO should it use useCallback()?
      if (latestRunning.current) return
      try {
        setRunning(true)
        return callback(...args)
      } finally {
        setRunning(false)
      }
    },
    [callback, latestRunning],
  )

  guardedCallback.running = running

  return guardedCallback
}

export default function KioskCheckIn(props) {
  const { organizationName, volunteeringRoles, timesheetMode, volunteers, setTimesheetMode, checkIn, checkOut } = props

  const [selected, setSelected] = useState(null)
  const [showDialog, setShowDialog] = useState(false)
  const [query, setQuery] = useState('')
  const search = useMiniSearch(volunteers, timesheetSearchConfig)
  const encodedOccurrence = useSelector(getEncodedOccurrence)
  const serverResults = useSelector(getSearchResults)
  const dispatch = useDispatch()
  // Kill-switch for the on-demand /volunteer_search flow. When OFF the kiosk
  // falls back to its legacy behaviour: the roster contains every org volunteer
  // and we filter that locally without any server-side search calls.
  const onDemandSearchEnabled = useFeatureEnabled('KioskOnDemandSearch')

  // Mirrors the debounce inside UserSearch so we kick off the on-demand
  // /volunteer_search request at the same cadence as the local miniSearch.
  // The saga uses takeLatest, so any in-flight request is cancelled when the
  // user keeps typing.
  const debouncedQuery = useDebounce(query, 300)
  useEffect(() => {
    if (!onDemandSearchEnabled) return
    const trimmed = debouncedQuery.trim()
    if (!trimmed) {
      dispatch(searchVolunteersCleared())
      return
    }
    dispatch(searchVolunteersRequested(trimmed))
  }, [debouncedQuery, dispatch, onDemandSearchEnabled])

  const handleSearch = useCallback(
    q => {
      const local = search(q)
      const trimmed = (q || '').trim()
      // Server-side results only matter when the feature is enabled and we
      // are in check-in mode. Check-out runs against the already-checked-in
      // list (which always comes from the roster) so we don't merge anything
      // there.
      if (!onDemandSearchEnabled || !trimmed || timesheetMode !== 'check-in') return local
      const localIds = new Set(local.map(v => get(v, 'user.id')))
      // The server already excludes anyone with an open active_volunteer
      // session (so no duplicates with the local roster's checked-in rows),
      // but we still de-dupe by user.id to keep the order stable: locally
      // known volunteers (with check-in/out state) come first, then anyone
      // the operator typed in but who isn't on the roster yet.
      const remote = serverResults.filter(v => !localIds.has(get(v, 'user.id')))
      return [...local, ...remote]
    },
    [search, serverResults, timesheetMode, onDemandSearchEnabled],
  )

  useKioskSearchDiagnostics({ query, search: handleSearch })

  const fulfillRequirementsPrompt = useShowPrompt(RequirementsModal)
  const showError = useErrorNotification()

  const classes = useStyles()

  // Memoized callbacks to prevent child component re-renders
  const handleRequirements = useCallback(
    async user => {
      try {
        const done = await fulfillRequirementsPrompt({
          person: user,
          filters: {
            personId: user.id,
            associatedEntity: { encodedOccurrence },
            action: PrerequisitesAction.EVENT_REGISTRATION,
            freshAnswerSet: false,
            excludeCompletedAnswerSets: true,
          },
          isAdmin: false,
        })
        return done
      } catch (error) {
        showError('Failed to load waivers and questionnaires', error)
        // TODO: confirm we we want to still continue https://civicchamps.atlassian.net/browse/CCA-1369
        return false
      }
    },
    [fulfillRequirementsPrompt, encodedOccurrence, showError],
  )

  const handleSetMode = useCallback(
    mode => {
      if (mode !== timesheetMode) {
        setQuery('')
        setSelected(null)
      }
      setTimesheetMode(mode)
    },
    [timesheetMode, setTimesheetMode],
  )

  const handleSelect = useCallback(volunteer => {
    // Use functional setState to avoid dependency on 'selected'
    // This keeps handleSelect stable and prevents UserListItem re-renders
    setSelected(prev => (prev?.id === volunteer.id ? null : volunteer))
  }, [])

  const handleSubmitUser = useRunningGuard(
    useCallback(async () => {
      if (timesheetMode === 'check-in') {
        if (await handleRequirements(selected.user)) {
          checkIn(selected)
          setSelected(null)
          setQuery('')
        }
      } else {
        setShowDialog(true)
      }
    }, [timesheetMode, handleRequirements, selected, checkIn]),
  )

  const handleSubmitCheckOut = useCallback(
    checkOutData => {
      checkOut(selected, checkOutData)
      setSelected(null)
      setQuery('')
      setShowDialog(false)
    },
    [selected, checkOut],
  )

  const handleCloseDialog = useCallback(() => {
    setShowDialog(false)
  }, [])

  return (
    <>
      <Grid container justify="flex-end">
        <Link to="/kiosk/signUp" className={classes.signUpButton}>
          Sign Up
        </Link>
      </Grid>
      <Container maxWidth="md" className={classes.containerWrapper}>
        <Grid container direction="column" justify="center" align="center" className={classes.containerHeader}>
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              {organizationName}
            </Typography>
            <Typography variant="subtitle1" gutterBottom className={classes.subTitle}>
              {`Type Name to ${timesheetMode === 'check-in' ? 'Check In' : 'Check Out'}`}
            </Typography>
          </Grid>
          <Grid item xs>
            <ToggleButton alignment={timesheetMode} setAlignment={handleSetMode} />
          </Grid>
        </Grid>
        <UserSearch
          timesheetMode={timesheetMode}
          query={query}
          selected={selected}
          onChange={setQuery}
          onSearch={handleSearch}
          onSelect={handleSelect}
          onSubmit={handleSubmitUser}
        />
      </Container>
      <CheckOutDialog
        open={showDialog}
        onClose={handleCloseDialog}
        volunteer={selected}
        volunteeringRoles={volunteeringRoles}
        onSubmit={handleSubmitCheckOut}
      />
    </>
  )
}
